import { Eye, MousePointerClick } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { type SalesLeadActivity, SalesLeadActivityType } from '@/models/salesLead'
import { ActivityAuthorIcon } from './ActivityAuthorIcon'
import { ActivityContent } from './ActivityContent'
import { activityMetaParts } from './activityMeta'
import { activityTypeStyles } from './activityTypeStyles'

const SMS_ACTIVITY_TYPES = new Set<SalesLeadActivityType>([
  SalesLeadActivityType.SMS_INBOUND,
  SalesLeadActivityType.SMS_OUTBOUND,
])

interface ActivityCardProps {
  activity: SalesLeadActivity
  className?: string
  /** Pinned to the card's top-right corner, like a note's edit button. */
  corner?: ReactNode
  /** At the end of the header line, like a Reply button. */
  headerEnd?: ReactNode
}

/** One timeline entry: its type, who and when, then its content. */
export function ActivityCard({ activity, className, corner, headerEnd }: ActivityCardProps) {
  const style = activityTypeStyles[activity.activityType]
  const Icon = style.icon
  const isOutboundEmail = activity.activityType === SalesLeadActivityType.EMAIL_OUTBOUND
  const isSms = SMS_ACTIVITY_TYPES.has(activity.activityType)

  return (
    <Card className={cn('relative transition-colors duration-500', className)}>
      {corner}
      <CardContent>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn('gap-1 px-2 py-0.5', style.className)}>
            <Icon className="size-3" />
            {style.label}
          </Badge>
          {isOutboundEmail && activity.emailOpened && (
            <Badge variant="outline" className="gap-1 border-green-200 px-2 py-0.5 text-green-700">
              <Eye className="size-3" />
              Opened
            </Badge>
          )}
          {isOutboundEmail && activity.emailHasLinks && activity.emailClicked && (
            <Badge variant="outline" className="gap-1 border-amber-200 px-2 py-0.5 text-amber-700">
              <MousePointerClick className="size-3" />
              Clicked
            </Badge>
          )}
          <span className="text-muted-foreground flex items-center text-sm">
            <ActivityAuthorIcon activity={activity} />
            <span>
              {activity.createdByName || 'System'} &middot;{' '}
              {new Date(activity.createdAt).toLocaleString(undefined, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
              {activityMetaParts(activity).map((part) => (
                <Fragment key={part}> &middot; {part}</Fragment>
              ))}
              {isSms && activity.smsStatus === 'failed' && (
                <>
                  {' '}
                  &middot; <span className="text-red-600">failed</span>
                </>
              )}
            </span>
          </span>
          {headerEnd}
        </div>
        <ActivityContent activity={activity} />
      </CardContent>
    </Card>
  )
}
